import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  ValveNode,
  ValveNodeState,
  ValveCommand,
  ValveControlOptions,
  ValveNodeWebSocketMessage,
  FilterType,
} from '@/types/valvenodes';
import { Device, Valve } from '@/types/api';
import { DeviceWithCommandState } from '@/types/api';
import { transformDeviceToValveNode, updateValveNodeFromDevice } from '@/types/valvenodes/transformers';
import { initializeValveNodeApi } from '@/services/valvenodes/api';
import { apiClient } from '@/services/apiClient';
import { fetchValvesAsync } from './deviceSlice';

const initialState: ValveNodeState = {
  nodes: {},
  selectedNodeId: null,
  loading: false,
  error: null,
  lastUpdated: 0,
  filters: {
    status: 'all',
    valveStatus: 'all',
    errors: 'all',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
};

// Async thunks
// Initialize the valve node API service
const initializeApiService = () => {
  // Create an axios-compatible wrapper around our apiClient
  const axiosCompatibleAdapter = {
    get: async (url: string, config?: any) => {
      const response = await apiClient.get(url, config);
      return {
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
      };
    },
    post: async (url: string, data?: any, config?: any) => {
      const response = await apiClient.post(url, data, config);
      return {
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
      };
    },
    put: async (url: string, data?: any, config?: any) => {
      const response = await apiClient.put(url, data, config);
      return {
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
      };
    },
    delete: async (url: string, config?: any) => {
      const response = await apiClient.delete(url, config);
      return {
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
      };
    },
    // Add other axios properties that might be needed
    defaults: {
      headers: apiClient.getAuthHeaders(),
    },
    interceptors: {
      request: { use: () => {} },
      response: { use: () => {} },
    },
  } as any;
  
  return initializeValveNodeApi(axiosCompatibleAdapter);
};

export const fetchValveNodesAsync = createAsyncThunk(
  'valveNodes/fetchValveNodes',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      
      // Ensure valves are fetched first
      const state = getState() as { devices: { valves: Valve[] } };
      const valvesAvailable = state.devices.valves && state.devices.valves.length > 0;
      
      if (!valvesAvailable) {
        console.log('🔍 DIAGNOSTIC: fetchValveNodesAsync - No valves found, fetching...');
        await dispatch(fetchValvesAsync());
      }
      
      // Get updated state after potential valve fetch
      const updatedState = getState() as { devices: { valves: Valve[] } };
      const valves = updatedState.devices.valves;
      
      
      const api = initializeApiService();
      const response = await api.getValveNodes();
      
      // Transform nodes with valve data to ensure aliases are included
      const nodes = response.data.nodes;
      const enhancedNodes = nodes.map((node: any) => {
        // Find the corresponding device to transform with valve data      
        const devicesState = (getState() as { devices: { list: any[] } }).devices.list;
        
        // Add safeguard to prevent undefined error
        if (!devicesState) {
          console.log('❌ DIAGNOSTIC: fetchValveNodesAsync - devicesState is undefined, returning original node');
          return node;
        }
        
        const device = devicesState.find((d: any) => d.dev_eui === node.id);
        
        if (device) {
          return transformDeviceToValveNode(device, { valves });
        } else {
          console.log('🔍 DIAGNOSTIC: fetchValveNodesAsync - No device found for node, using original node:', {
            nodeId: node.id
          });
          return node;
        }
      });
      
      return enhancedNodes;
    } catch (error: any) {
      console.error('❌ DIAGNOSTIC: fetchValveNodesAsync - API call failed:', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch valve nodes');
    }
  }
);

export const fetchValveNodeAsync = createAsyncThunk(
  'valveNodes/fetchValveNode',
  async (nodeId: string, { rejectWithValue }) => {
    try {
      const api = initializeApiService();
      const response = await api.getValveNode(nodeId);
      return response.data.node;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch valve node');
    }
  }
);

export const controlValveAsync = createAsyncThunk(
  'valveNodes/controlValve',
  async (
    { nodeId, valveId, action, options }: {
      nodeId: string;
      valveId: number;
      action: 'open' | 'close';
      options?: ValveControlOptions;
    },
    { rejectWithValue }
  ) => {
    try {
      const command: ValveCommand = {
        valve: valveId,
        action,
        ...(options?.timeControl && {
          timeControlSeconds: options.timeControl.hours * 3600 + options.timeControl.minutes * 60
        }),
        ...(options?.flowControl && {
          flowControlPulses: options.flowControl.pulses
        }),
        ...(options?.untilClosed && action === 'open' && { untilClosed: true }),
      };

      const api = initializeApiService();
      const response = await api.controlValve(nodeId, command);
      return { nodeId, valveId, action, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to control valve');
    }
  }
);

export const syncFromDevicesAsync = createAsyncThunk(
  'valveNodes/syncFromDevices',
  async (devices: Device[], { rejectWithValue, getState, dispatch }) => {
    try {
      
      // Ensure valves are fetched
      const state = getState() as { devices: { valves: Valve[] } };

      
      if (state.devices.valves.length === 0) {
        console.log('🔍 DIAGNOSTIC: syncFromDevicesAsync - No valves found, fetching...');
        const valvesResult = await dispatch(fetchValvesAsync());
        console.log('🔍 DIAGNOSTIC: syncFromDevicesAsync - Valves fetch result', {
          fulfilled: fetchValvesAsync.fulfilled.match(valvesResult),
          payload: valvesResult.payload
        });
      }
      
      // Get valve data from state after potential fetch
      const updatedState = getState() as { devices: { valves: Valve[] } };
      const valves = updatedState.devices.valves;
      
      // Transform devices to valve nodes with valve data
      const valveNodes = devices.map(device => {
        
        const transformedNode = transformDeviceToValveNode(device, { valves });

        return transformedNode;
      });
      
      
      return valveNodes;
    } catch (error: any) {
      console.error('❌ DIAGNOSTIC: syncFromDevicesAsync - Sync failed', error);
      return rejectWithValue(error.message || 'Failed to sync devices to valve nodes');
    }
  }
);

const valveNodeSlice = createSlice({
  name: 'valveNodes',
  initialState,
  reducers: {
    selectNode: (state, action: PayloadAction<string>) => {
      state.selectedNodeId = action.payload;
    },
    clearSelectedNode: (state) => {
      state.selectedNodeId = null;
    },
    updateNode: (state, action: PayloadAction<ValveNode>) => {
      const node = action.payload;
      state.nodes[node.id] = node;
      state.lastUpdated = Date.now();
    },
    updateNodeFromWebSocket: (state, action: PayloadAction<ValveNodeWebSocketMessage>) => {
      const { nodeId, payload } = action.payload;
      const existingNode = state.nodes[nodeId];
      
      if (existingNode) {
        // Update existing node with new data
        state.nodes[nodeId] = {
          ...existingNode,
          ...payload,
        };
        state.lastUpdated = Date.now();
      }
    },
    updateNodeFromDevice: (state, action: PayloadAction<{ nodeId: string; device: Partial<Device> }>) => {
      const { nodeId, device } = action.payload;
      const existingNode = state.nodes[nodeId];
      
      if (existingNode) {
        state.nodes[nodeId] = updateValveNodeFromDevice(existingNode, device);
        state.lastUpdated = Date.now();
      }
    },
    setFilter: (state, action: PayloadAction<{ filterType: keyof typeof initialState.filters; value: FilterType }>) => {
      const { filterType, value } = action.payload;
      state.filters[filterType] = value as any;
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        valveStatus: 'all',
        errors: 'all',
      };
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticValveUpdate: (state, action: PayloadAction<{
      nodeId: string;
      valveId: number;
      valveAction: 'open' | 'close';
    }>) => {
      const { nodeId, valveId, valveAction } = action.payload;
      const node = state.nodes[nodeId];
      
      if (node) {
        const updatedValves = node.valves.map(valve => {
          if (valve.id === valveId) {
            return {
              ...valve,
              status: valveAction as 'open' | 'closed',
            };
          }
          return valve;
        });
        
        state.nodes[nodeId] = {
          ...node,
          valves: updatedValves,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch valve nodes
      .addCase(fetchValveNodesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValveNodesAsync.fulfilled, (state, action) => {
        state.loading = false;
        const nodes = action.payload;
        
        // Convert array to object with ID as key
        const nodesObject: Record<string, ValveNode> = {};
        nodes.forEach((node: ValveNode) => {
          nodesObject[node.id] = node;
        });
        
        state.nodes = nodesObject;
        state.lastUpdated = Date.now();
        state.pagination.total = nodes.length;
      })
      .addCase(fetchValveNodesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch single valve node
      .addCase(fetchValveNodeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValveNodeAsync.fulfilled, (state, action) => {
        state.loading = false;
        const node = action.payload as unknown as ValveNode;
        state.nodes[node.id] = node;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchValveNodeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Control valve
      .addCase(controlValveAsync.pending, (state, action) => {
        // Optimistic update
        const { nodeId, valveId, action: valveAction } = action.meta.arg;
        const node = state.nodes[nodeId];
        
        if (node) {
          const updatedValves = node.valves.map(valve => {
            if (valve.id === valveId) {
              return {
                ...valve,
                status: valveAction as 'open' | 'closed',
              };
            }
            return valve;
          });
          
          state.nodes[nodeId] = {
            ...node,
            valves: updatedValves,
          };
        }
      })
      .addCase(controlValveAsync.fulfilled, (state, action) => {
        const { nodeId, valveId, action: valveAction, response } = action.payload;
        console.log('🔄 DIAGNOSTIC: controlValveAsync.fulfilled:', { nodeId, valveId, valveAction, response });
        
        // If we have a real command_id from the API, update the pending command state
        const commandId = (response as any)?.command_id || (response as any)?.data?.command_id;
        console.log('🔄 DIAGNOSTIC: Extracted command ID from response:', commandId);
        
        if (commandId) {
          console.log('🔄 DIAGNOSTIC: Replacing pending command with real command ID:', commandId);
          
          // Try to find and update the pending command in the devices store
          // This will be handled by the deviceSlice, but we need to trigger it
          setTimeout(() => {
            console.log('🔄 DIAGNOSTIC: Executing command replacement timeout');
            
            // Import and dispatch the updateValveCommandState action to replace the pending command
            import('./deviceSlice').then(({ updateValveCommandState }) => {
              console.log('🔄 DIAGNOSTIC: DeviceSlice imported successfully');
              
              // Get the stored pending command info
              const pendingCommandId = (window as any).__pendingCommandId;
              const pendingValveNumber = (window as any).__pendingValveNumber;
              const pendingDeviceId = (window as any).__pendingDeviceId;
              
              console.log('🔄 DIAGNOSTIC: Retrieved pending command info:', {
                pendingCommandId,
                pendingValveNumber,
                pendingDeviceId
              });
              
              if (pendingCommandId && pendingValveNumber && pendingDeviceId) {
                console.log('🔄 DIAGNOSTIC: Found pending command, replacing with real ID');
                
                // Create the real command with the actual command_id from API
                const realCommand = {
                  command_id: commandId,
                  device_id: pendingDeviceId,
                  valve_number: pendingValveNumber,
                  action: valveAction,
                  current_state: 'manual_control_sent' as const,
                  created_at: new Date().toISOString(),
                  retry_count: 0,
                  max_retries: 3,
                  source: 'api_response'
                };
                
                console.log('🔄 DIAGNOSTIC: Real command object created:', realCommand);
                
                // Dispatch update to replace pending command with real command
                const dispatchResult = (window as any).__reduxDispatch(updateValveCommandState({
                  deviceId: pendingDeviceId,
                  devEui: nodeId,
                  valveNumber: pendingValveNumber,
                  command: realCommand
                }));
                
                console.log('🔄 DIAGNOSTIC: Command replacement dispatch result:', dispatchResult);
                console.log('🔄 DIAGNOSTIC: Pending command replaced with real command:', realCommand);
                
                // Clear the pending command info
                delete (window as any).__pendingCommandId;
                delete (window as any).__pendingValveNumber;
                delete (window as any).__pendingDeviceId;
                console.log('🔄 DIAGNOSTIC: Pending command info cleared');
              } else {
                console.log('❌ DIAGNOSTIC: Missing pending command info - cannot replace command');
              }
            }).catch((importError) => {
              console.error('❌ DIAGNOSTIC: Failed to import deviceSlice:', importError);
            });
          }, 100); // Small delay to ensure the API response is processed
        } else {
          console.log('❌ DIAGNOSTIC: No command_id found in API response:', response);
        }
        
        const node = state.nodes[nodeId];
        
        if (node) {
          // Update the valve status to match the confirmed action
          const updatedValves = node.valves.map(valve => {
            if (valve.id === valveId) {
              return {
                ...valve,
                status: valveAction as 'open' | 'closed',
              };
            }
            return valve;
          });
          
          state.nodes[nodeId] = {
            ...node,
            valves: updatedValves,
          };
        }
      })
      .addCase(controlValveAsync.rejected, (state, action) => {
        // Revert optimistic update on error
        const { nodeId, valveId, action: valveAction } = action.meta.arg;
        const node = state.nodes[nodeId];
        
        if (node) {
          const updatedValves = node.valves.map(valve => {
            if (valve.id === valveId) {
              // Revert to opposite status
              return {
                ...valve,
                status: (valveAction === 'open' ? 'closed' : 'open') as 'open' | 'closed',
              };
            }
            return valve;
          });
          
          state.nodes[nodeId] = {
            ...node,
            valves: updatedValves,
          };
        }
        
        state.error = action.payload as string;
      })
      
      // Sync from devices
      .addCase(syncFromDevicesAsync.fulfilled, (state, action) => {
        const nodes = action.payload;
        
        // Convert array to object with ID as key
        const nodesObject: Record<string, ValveNode> = {};
        nodes.forEach((node: ValveNode) => {
          nodesObject[node.id] = node;
        });
        
        state.nodes = nodesObject;
        state.lastUpdated = Date.now();
        state.pagination.total = nodes.length;
      });
  },
});

// Selectors
export const selectValveNodes = (state: { valveNodes: ValveNodeState }) => state.valveNodes.nodes;
export const selectValveNode = (nodeId: string) => (state: { valveNodes: ValveNodeState }) => 
  state.valveNodes.nodes[nodeId];
export const selectSelectedValveNode = (state: { valveNodes: ValveNodeState }) => 
  state.valveNodes.selectedNodeId ? state.valveNodes.nodes[state.valveNodes.selectedNodeId] : null;
export const selectValveNodesArray = (state: { valveNodes: ValveNodeState }) => 
  Object.values(state.valveNodes.nodes);
export const selectFilteredValveNodes = (state: { valveNodes: ValveNodeState }) => {
  const { nodes, filters } = state.valveNodes;
  const nodesArray = Object.values(nodes);
  
  return nodesArray.filter(node => {
    // Status filter
    if (filters.status === 'online' && !node.status.online) return false;
    if (filters.status === 'offline' && node.status.online) return false;
    
    // Valve status filter
    if (filters.valveStatus === 'active') {
      const hasActiveValve = node.valves.some(valve => valve.status === 'open');
      if (!hasActiveValve) return false;
    }
    if (filters.valveStatus === 'inactive') {
      const hasActiveValve = node.valves.some(valve => valve.status === 'open');
      if (hasActiveValve) return false;
    }
    
    // Error filter
    if (filters.errors === 'withErrors' && node.errors.length === 0) return false;
    if (filters.errors === 'withoutErrors' && node.errors.length > 0) return false;
    
    return true;
  });
};
export const selectValveNodesLoading = (state: { valveNodes: ValveNodeState }) => state.valveNodes.loading;
export const selectValveNodesError = (state: { valveNodes: ValveNodeState }) => state.valveNodes.error;
export const selectValveNodesFilters = (state: { valveNodes: ValveNodeState }) => state.valveNodes.filters;
export const selectValveNodesPagination = (state: { valveNodes: ValveNodeState }) => state.valveNodes.pagination;

export const {
  selectNode,
  clearSelectedNode,
  updateNode,
  updateNodeFromWebSocket,
  updateNodeFromDevice,
  setFilter,
  clearFilters,
  setPagination,
  clearError,
  optimisticValveUpdate,
} = valveNodeSlice.actions;

// Selectors for valve aliases
export const selectValveAlias = (nodeId: string, valveNumber: number) => (state: { valveNodes: ValveNodeState, devices: { list: DeviceWithCommandState[], valves: Valve[] } }) => {
  const node = state.valveNodes.nodes[nodeId];
  if (!node) return null;
  
  const valve = node.valves.find(v => v.id === valveNumber);
  
  // Try to get valve alias from devices state
  const deviceValves = state.devices.valves || [];
  
  // Handle different possible structures of deviceValves
  let valvesArray: Valve[] = [];
  if (Array.isArray(deviceValves)) {
    valvesArray = deviceValves;
  } else if (deviceValves && (deviceValves as any).valves && Array.isArray((deviceValves as any).valves)) {
    valvesArray = (deviceValves as any).valves;
  } else {
    return valve?.name || `Valve ${valveNumber}`;
  }
  
  // Find the device first, then match by device_id
  const device = state.devices.list.find(d => d.dev_eui === nodeId);
  if (device) {
    const deviceValve = valvesArray.find(v => v.device_id === device.id && v.valve_number === valveNumber);
    if (deviceValve) {
      return deviceValve.name;
    }
  }
  
  // Fall back to valve name or default
  return valve?.name || `Valve ${valveNumber}`;
};

export const selectNodeWithValveAliases = (nodeId: string) => (state: { valveNodes: ValveNodeState, devices: { list: DeviceWithCommandState[], valves: Valve[] } }) => {
  const node = state.valveNodes.nodes[nodeId];
  if (!node) {
    console.log('🔍 DIAGNOSTIC: selectNodeWithValveAliases - No node found for ID:', nodeId);
    return null;
  }
  
  const deviceValves = state.devices.valves || [];
  
  
  // Find the device first to get the correct device_id
  const device = state.devices.list.find(d => d.dev_eui === nodeId);
  if (!device) {
    console.log('🔍 DIAGNOSTIC: selectNodeWithValveAliases - No device found for dev_eui:', nodeId);
    return node; // Return original node without aliases
  }
  
  
  // Handle different possible structures of deviceValves
  let valvesArray: Valve[] = [];
  if (Array.isArray(deviceValves)) {
    valvesArray = deviceValves;
  } else if (deviceValves && (deviceValves as any).valves && Array.isArray((deviceValves as any).valves)) {
    valvesArray = (deviceValves as any).valves;
  } else {
    console.log('🔍 DIAGNOSTIC: selectNodeWithValveAliases - Invalid deviceValves structure:', deviceValves);
    return node; // Return original node without aliases
  }
  
  // Create enhanced valves with aliases
  const enhancedValves = node.valves.map(valve => {
    const deviceValve = valvesArray.find(v => v.device_id === device.id && v.valve_number === valve.id);
    const enhancedValve = {
      ...valve,
      name: deviceValve?.name || valve.name,
      alias: deviceValve?.name || null,
    };
    
    
    return enhancedValve;
  });
  
  const enhancedNode = {
    ...node,
    valves: enhancedValves,
  };

  
  return enhancedNode;
};

export default valveNodeSlice.reducer;